/* ===========================================================================
   MEDIA SWITCHER — runtime
   ---------------------------------------------------------------------------
   Custom element sin dependencias. Al atar el ciclo de vida al del DOM, el
   Theme Editor (que reinyecta la sección entera) queda cubierto sin escuchar
   shopify:section:load ni llevar registro de instancias.

   El patrón de accesibilidad es el de pestañas de la APG: una sola parada de
   tabulación en el grupo y navegación con flechas dentro.
   =========================================================================== */

(function () {
  'use strict';

  if (customElements.get('media-switcher')) return;

  var KEYS = {
    left: 'ArrowLeft',
    right: 'ArrowRight',
    up: 'ArrowUp',
    down: 'ArrowDown',
    home: 'Home',
    end: 'End',
  };

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class MediaSwitcher extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-ms-tab]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ms-panel]'));
      if (!this.tabs.length) return;

      this.warmed = false;
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      /* El editor avisa cuando el comerciante selecciona un bloque en el panel
         lateral; sin esto tendría que buscar a mano la pestaña que acaba de
         tocar para verla. */
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      var initial = this.tabs.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      this.select(initial < 0 ? 0 : initial, { focus: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    handleClick(event) {
      var tab = event.target.closest('[data-ms-tab]');
      if (!tab || !this.contains(tab)) return;
      this.select(this.tabs.indexOf(tab), { focus: false });
      this.warmRemainingMedia();
    }

    handleKeydown(event) {
      var tab = event.target.closest('[data-ms-tab]');
      if (!tab || !this.contains(tab)) return;

      var current = this.tabs.indexOf(tab);
      var last = this.tabs.length - 1;
      var next = null;

      if (event.key === KEYS.left || event.key === KEYS.up) next = current <= 0 ? last : current - 1;
      else if (event.key === KEYS.right || event.key === KEYS.down) next = current >= last ? 0 : current + 1;
      else if (event.key === KEYS.home) next = 0;
      else if (event.key === KEYS.end) next = last;

      if (next === null) return;
      event.preventDefault();
      this.select(next, { focus: true });
      this.warmRemainingMedia();
    }

    handleBlockSelect(event) {
      var panel = event.target.closest ? event.target.closest('[data-ms-panel]') : null;
      var index = panel ? this.panels.indexOf(panel) : -1;

      /* El evento lo dispara el bloque, que aquí es el panel; si no lo es,
         puede venir de otra sección de la página. */
      if (index < 0) {
        index = this.panels.findIndex(function (p) {
          return p === event.target || p.contains(event.target);
        });
      }
      if (index >= 0) this.select(index, { focus: false });
    }

    select(index, options) {
      var focus = options && options.focus;

      this.tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        /* Una sola parada de tabulación: el resto se alcanza con las flechas. */
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (active && focus) tab.focus();
      });

      this.panels.forEach(function (panel, i) {
        var active = i === index;
        if (active) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');

        var video = panel.querySelector('video');
        if (!video) return;

        if (active) {
          if (video.autoplay && !prefersReducedMotion()) {
            var played = video.play();
            /* Safari rechaza la promesa si el vídeo no está silenciado o la
               pestaña está en segundo plano: sin este catch salta un error no
               capturado en consola. */
            if (played && played.catch) played.catch(function () {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    /* Los medios que no se ven de salida van con loading="lazy", así que la
       página inicial solo descarga uno. En cuanto el visitante demuestra
       interés cambiando de elemento, se piden los que faltan para que los
       siguientes cambios sean instantáneos y no muestren un hueco vacío.

       Ojo: esto NO puede llamarse desde select(), porque select() también
       corre al montar el elemento y entonces la página cargaría todos los
       medios de golpe, que es justo lo que se quiere evitar. Solo lo disparan
       las interacciones reales del visitante. */
    warmRemainingMedia() {
      if (this.warmed) return;
      this.warmed = true;

      this.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.setAttribute('loading', 'eager');
      });
      this.querySelectorAll('video[preload="none"]').forEach(function (video) {
        video.setAttribute('preload', 'metadata');
      });
    }
  }

  customElements.define('media-switcher', MediaSwitcher);
})();
